/**
 * Date range control for event reports.
 *
 * Users pick INCLUSIVE dates ("May 1 to May 31"); the API converts to the
 * half-open [start, end) the queries use. Snapshot reports do not render this at
 * all — they have no period, and showing a date picker that does nothing is worse
 * than showing none.
 *
 * Calendar days are America/Chicago, matching the reporting engine. UTC date
 * getters would shift "this month" / "last month" around midnight CT.
 */
import { useState } from "react";

export interface Range { start: string; end: string }

const TZ = "America/Chicago";

export function chicagoDay(d: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(d);
  const g = (t: string) => parts.find(p => p.type === t)?.value ?? "";
  return `${g("year")}-${g("month")}-${g("day")}`;
}

function addCalendarDays(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + days));
  return `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, "0")}-${String(next.getUTCDate()).padStart(2, "0")}`;
}

function firstOfMonth(iso: string): string {
  return `${iso.slice(0, 7)}-01`;
}

export function lastCompleteMonth(): Range {
  const today = chicagoDay();
  const thisFirst = firstOfMonth(today);
  const lastDayPrev = addCalendarDays(thisFirst, -1);
  return { start: firstOfMonth(lastDayPrev), end: lastDayPrev };
}

export function last90Days(): Range {
  const today = chicagoDay();
  return { start: addCalendarDays(today, -89), end: today };
}

const PRESETS: { label: string; get: () => Range }[] = [
  { label: "Last month", get: lastCompleteMonth },
  { label: "This month", get: () => {
      const today = chicagoDay();
      return { start: firstOfMonth(today), end: today };
    } },
  { label: "Last 90 days", get: last90Days },
  { label: "Year to date", get: () => {
      const today = chicagoDay();
      return { start: `${today.slice(0, 4)}-01-01`, end: today };
    } },
];

export function ReportDateFilter({ value, onChange }: { value: Range; onChange: (r: Range) => void }) {
  const [custom, setCustom] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-2">
      {custom ? (
        <>
          <input type="date" value={value.start} aria-label="Start date"
                 onChange={e => onChange({ ...value, start: e.target.value })}
                 className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
          <span className="text-gray-500 text-sm">to</span>
          <input type="date" value={value.end} aria-label="End date"
                 onChange={e => onChange({ ...value, end: e.target.value })}
                 className="border border-gray-300 rounded-md px-3 py-2 text-sm" />
          <button onClick={() => setCustom(false)} className="text-sm text-indigo-600">Presets</button>
        </>
      ) : (
        <div className="flex flex-wrap gap-1" role="group" aria-label="Date range">
          {PRESETS.map(p => {
            const r = p.get();
            const active = r.start === value.start && r.end === value.end;
            return (
              <button key={p.label} onClick={() => onChange(r)} aria-pressed={active}
                className={`px-3 py-1.5 text-sm rounded-md border ${active
                  ? "bg-gray-800 text-white border-gray-800"
                  : "border-gray-300 text-gray-600 hover:bg-gray-50"}`}>{p.label}</button>
            );
          })}
          <button onClick={() => setCustom(true)}
            className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50">
            Custom…
          </button>
        </div>
      )}
      <span className="text-sm text-gray-500">{value.start} to {value.end} CT</span>
    </div>
  );
}
